import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { usePractice } from "../context/PracticeContext";
import { contextsApi, type ResolvedContext } from "../api/contexts";
import { PageSkeleton } from "../components/PageSkeleton";
import { EmptyState } from "../components/EmptyState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollText, Pencil, Save, RotateCcw, Loader2, X } from "lucide-react";

export function AdminContexts() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const queryClient = useQueryClient();
  const { selectedPracticeId } = usePractice();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftFragment, setDraftFragment] = useState("");
  const [draftRules, setDraftRules] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    setBreadcrumbs([{ label: "Administration" }, { label: "Agent Contexts" }]);
  }, [setBreadcrumbs]);

  const queryKey = ["admin", "contexts", selectedPracticeId] as const;

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => contextsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const save = useMutation({
    mutationFn: (vars: { id: string; systemPromptFragment: string; rules: string[] }) => {
      setFeedback(null);
      return contextsApi.update(selectedPracticeId!, vars.id, {
        systemPromptFragment: vars.systemPromptFragment,
        rules: vars.rules,
      });
    },
    onSuccess: ({ context }) => {
      setFeedback(`Saved "${context.name}".`);
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (e: unknown) => setFeedback(`Save failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  const reset = useMutation({
    mutationFn: (id: string) => {
      setFeedback(null);
      return contextsApi.reset(selectedPracticeId!, id);
    },
    onSuccess: ({ context }) => {
      setFeedback(`Reset "${context.name}" to default.`);
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (e: unknown) => setFeedback(`Reset failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  function startEdit(ctx: ResolvedContext) {
    setEditingId(ctx.id);
    setDraftFragment(ctx.systemPromptFragment);
    setDraftRules(ctx.rules.join("\n"));
  }

  if (!selectedPracticeId) {
    return (
      <div className="p-6">
        <EmptyState icon={ScrollText} message="Select a practice to edit its agent contexts." />
      </div>
    );
  }

  if (isLoading) return <PageSkeleton variant="list" />;

  const contexts = data?.contexts ?? [];

  return (
    <div className="space-y-4 p-6 max-w-5xl mx-auto">
      <div>
        <h2 className="text-xl font-bold">Agent Contexts</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Edit the system-prompt fragment and rules each context contributes to an agent. Changes
          are saved per practice; reset reverts to the shipped default.
        </p>
      </div>

      {feedback && (
        <div className="rounded-md border border-border bg-muted/30 px-4 py-2 text-sm">{feedback}</div>
      )}

      {contexts.length === 0 && (
        <EmptyState icon={ScrollText} message="No contexts available." />
      )}

      {contexts.map((ctx) => {
        const isEditing = editingId === ctx.id;
        return (
          <div key={ctx.id} className="rounded-lg border border-border bg-card p-4 space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">{ctx.name}</h3>
                  {ctx.isOverridden && <Badge variant="secondary">Customized</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{ctx.description}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {ctx.requiredConnectors.map((c) => (
                    <Badge key={c} variant="outline" className="text-[10px]">
                      {c}
                    </Badge>
                  ))}
                </div>
              </div>
              {!isEditing && (
                <Button size="sm" variant="ghost" onClick={() => startEdit(ctx)}>
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  Edit
                </Button>
              )}
            </div>

            {isEditing && (
              <div className="space-y-3 pt-3 border-t border-border">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground">System prompt fragment</label>
                  <Textarea
                    value={draftFragment}
                    onChange={(e) => setDraftFragment(e.target.value)}
                    placeholder="## Context heading&#10;What the agent does in this domain…"
                    rows={9}
                    disabled={save.isPending}
                    className="mt-1 font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground">Rules (one per line)</label>
                  <Textarea
                    value={draftRules}
                    onChange={(e) => setDraftRules(e.target.value)}
                    placeholder="One rule per line…"
                    rows={6}
                    disabled={save.isPending}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      save.mutate({
                        id: ctx.id,
                        systemPromptFragment: draftFragment,
                        rules: draftRules.split("\n").map((r) => r.trim()).filter(Boolean),
                      })
                    }
                    disabled={save.isPending}
                  >
                    {save.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Save className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                    <X className="h-3.5 w-3.5 mr-1.5" />
                    Cancel
                  </Button>
                  {ctx.isOverridden && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto text-muted-foreground"
                      onClick={() => reset.mutate(ctx.id)}
                      disabled={reset.isPending}
                    >
                      {reset.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Reset to default
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
